/**
 * LessonQuiz — 5-question MCQ shown at the bottom of a lesson Part.
 *
 * UX flow:
 *   1. Idle  → "Test your understanding" button (collapsed).
 *   2. Click → generate (or fetch cached) quiz via POST /lesson/quiz.
 *   3. User answers each MCQ; option chips highlight on click.
 *   4. Submit → POST /lesson/quiz/grade → shows score, per-question
 *      explanation, and (if score ≥ 60%) bumps reading streak.
 *
 * Tone & design
 *   • Glass card, sits below the lesson body so it never blocks reading.
 *   • Calm — green for correct, soft amber for wrong, no shaming reds.
 *   • One retry per visit if the user fails.
 *
 * Cost protection
 *   The quiz is cached server-side per (user, path, step, part) so
 *   re-opening doesn't re-spend an LLM call.
 */
import { useState } from "react";
import { Brain, CheckCircle2, XCircle, Loader2, RefreshCw, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function LessonQuiz({ pathId, stepIndex, part, onStreakUpdated }) {
  const [phase, setPhase] = useState("idle"); // idle | loading | playing | graded
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  if (!pathId || stepIndex == null || !part) return null;

  const start = async () => {
    setPhase("loading");
    try {
      const { data } = await api.post("/lesson/quiz", {
        path_id: pathId,
        step_index: stepIndex,
        part,
      });
      setQuestions(data.questions || []);
      setAnswers(new Array((data.questions || []).length).fill(null));
      setResult(null);
      setPhase("playing");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate quiz");
      setPhase("idle");
    }
  };

  const submit = async () => {
    if (answers.some((a) => a === null)) {
      toast.error("Please answer all 5 questions");
      return;
    }
    setPhase("loading");
    try {
      const { data } = await api.post("/lesson/quiz/grade", {
        path_id: pathId,
        step_index: stepIndex,
        part,
        answers,
      });
      setResult(data);
      setPhase("graded");
      if (data.streak) onStreakUpdated?.(data.streak);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grading failed");
      setPhase("playing");
    }
  };

  const retry = () => {
    setAnswers(new Array(questions.length).fill(null));
    setResult(null);
    setPhase("playing");
  };

  // ─── IDLE / LOADING ────────────────────────────────────────────────
  if (phase === "idle" || phase === "loading") {
    return (
      <div className="mt-8 mb-6 p-5 rounded-2xl border border-white/10 bg-white/[0.03] flex items-center gap-4" data-testid="quiz-card">
        <div className="h-11 w-11 grid place-items-center rounded-xl bg-[#00FF9D]/15 border border-[#00FF9D]/30">
          <Brain className="text-[#9bffcc]" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm text-white">Test your understanding</div>
          <div className="text-[12px] text-white/55 mt-0.5">
            5 quick questions · ~2 min · keeps your reading streak alive
          </div>
        </div>
        <button
          onClick={start}
          disabled={phase === "loading"}
          className="px-4 py-2 rounded-full btn-primary font-display text-sm flex items-center gap-2 disabled:opacity-50"
          data-testid="btn-quiz-start"
        >
          {phase === "loading" ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />}
          {phase === "loading" ? "Generating…" : "Start quiz"}
        </button>
      </div>
    );
  }

  // ─── GRADED ────────────────────────────────────────────────────────
  if (phase === "graded" && result) {
    const passed = result.passed;
    return (
      <div className="mt-8 mb-6 rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="quiz-card">
        <div className={`p-5 flex items-center gap-4 ${passed ? "bg-[#00FF9D]/[0.08]" : "bg-amber-500/[0.08]"}`}>
          <div className={`h-11 w-11 grid place-items-center rounded-xl border ${passed ? "bg-[#00FF9D]/15 border-[#00FF9D]/30" : "bg-amber-500/15 border-amber-500/30"}`}>
            {passed ? <Trophy className="text-[#9bffcc]" size={20} /> : <RefreshCw className="text-amber-300" size={20} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm text-white">
              {passed ? "Excellent!" : "Almost — try once more"} · {result.score_pct}%
            </div>
            <div className="text-[12px] text-white/65 mt-0.5">
              {result.correct_count} of {result.total} correct
              {passed && result.streak ? ` · 🔥 ${result.streak.current}-day streak` : ""}
            </div>
          </div>
          {!passed && (
            <button
              onClick={retry}
              className="px-3.5 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-white text-xs font-display"
              data-testid="btn-quiz-retry"
            >
              Retry
            </button>
          )}
        </div>
        <div className="p-5 space-y-4">
          {result.detail.map((d, i) => (
            <div key={i} className="text-[13px]" data-testid={`quiz-result-${i}`}>
              <div className="flex items-start gap-2">
                {d.is_correct
                  ? <CheckCircle2 className="text-[#9bffcc] mt-0.5 flex-shrink-0" size={14} />
                  : <XCircle className="text-amber-300 mt-0.5 flex-shrink-0" size={14} />}
                <div className="flex-1">
                  <div className="text-white/85">{i + 1}. {d.q}</div>
                  {!d.is_correct && (
                    <div className="text-[11px] text-white/55 mt-1">{d.explanation}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─── PLAYING ───────────────────────────────────────────────────────
  return (
    <div className="mt-8 mb-6 p-5 rounded-2xl border border-white/10 bg-white/[0.03] space-y-5" data-testid="quiz-card">
      <div className="flex items-center gap-3">
        <Brain className="text-[#9bffcc]" size={18} />
        <div className="font-display text-sm text-white">Test your understanding</div>
        <div className="ml-auto text-[11px] text-white/45 font-mono-oak">
          {answers.filter((a) => a !== null).length} / {questions.length}
        </div>
      </div>
      {questions.map((q, qi) => (
        <div key={qi} data-testid={`quiz-q-${qi}`}>
          <div className="text-[13px] text-white/85 mb-2">{qi + 1}. {q.q}</div>
          <div className="grid grid-cols-1 gap-1.5">
            {q.options.map((opt, oi) => {
              const picked = answers[qi] === oi;
              return (
                <button
                  key={oi}
                  onClick={() => {
                    const next = [...answers];
                    next[qi] = oi;
                    setAnswers(next);
                  }}
                  className={`text-left text-[12.5px] px-3 py-2 rounded-lg border transition ${
                    picked
                      ? "border-[#00FF9D]/50 bg-[#00FF9D]/[0.10] text-white"
                      : "border-white/10 bg-white/[0.02] text-white/70 hover:bg-white/[0.05] hover:text-white/90"
                  }`}
                  data-testid={`quiz-q-${qi}-opt-${oi}`}
                >
                  <span className="text-white/40 mr-2 font-mono-oak">{String.fromCharCode(65 + oi)}.</span>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <button
        onClick={submit}
        disabled={answers.some((a) => a === null)}
        className="w-full py-2.5 rounded-xl btn-primary font-display font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40"
        data-testid="btn-quiz-submit"
      >
        <CheckCircle2 size={14} /> Submit
      </button>
    </div>
  );
}
